import type { MarketplaceItemId, OfferPriceConfig } from "@/services/marketplace/item";
import { whenRefAvailable } from "@/utilities";
import type { Ref } from "vue";
import * as PostHog from "@/services/analytics/postHog";
import { useUser } from "@/composables/user";
import { createLoginLink } from "@/router";
import { useRoute, useRouter } from "vue-router";
import type { CollectionItemId } from "@/services/collection/item";
import type DialogOfferPrice from "@/components/collectionItem/public/offerPrice/DialogOfferPrice.vue";

type PendingOpenAfterLoginConfig = {
  itemId: CollectionItemId | MarketplaceItemId;
};

const pendingOpenAfterLogin = ref<PendingOpenAfterLoginConfig>();

export function useOfferPriceDialog(dialog: Ref<InstanceType<typeof DialogOfferPrice> | null>, options: OfferPriceConfig) {
  const { isUserLoggedIn } = useUser();

  const router = useRouter();
  const route = useRoute();

  const open = () => {
    PostHog.trackEvent(PostHog.EventName.Marketplace_OfferPriceOpenDialog_Click);

    if (isUserLoggedIn.value) {
      whenRefAvailable(dialog).then((d) => {
        d.value.open(options);
      });
    } else {
      requireLogin();
    }
  };

  const requireLogin = () => {
    const storeConfig = {
      itemId: options.itemId,
    };
    usePendingOfferPriceDialog(storeConfig).storePendingOpen();

    const redirect = route.fullPath;
    const loginRoute = createLoginLink(redirect);
    router.push(loginRoute);
  };

  return {
    open,
  };
}

type PendingOfferPriceDialogOptions = {
  itemId: CollectionItemId | MarketplaceItemId;
};

export function usePendingOfferPriceDialog(options: PendingOfferPriceDialogOptions) {
  const isWaitingForLogin = computed(() => {
    const stored = pendingOpenAfterLogin.value;
    return stored !== undefined && stored.itemId === options.itemId;
  });

  const storePendingOpen = () => {
    pendingOpenAfterLogin.value = {
      itemId: options.itemId,
    };
  };

  const clearPendingOpen = () => {
    pendingOpenAfterLogin.value = undefined;
  };

  return {
    isWaitingForLogin,
    storePendingOpen,
    clearPendingOpen,
  };
}
